"use client";

import Link from "next/link";
import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/cn";
import {
  audienceLabels,
  fromPrice,
  type Audience,
  type Product,
} from "@/lib/products";

type Filter = "all" | Audience;

const filters: { id: Filter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "developers", label: audienceLabels.developers },
  { id: "creators", label: audienceLabels.creators },
];

const toneClass: Record<Product["tone"], string> = {
  mist: "bg-mist",
  butter: "bg-butter",
  blush: "bg-blush",
  mint: "bg-mint",
};

/** Small decorative preview. Developers get a page, creators a tile grid. */
function ProductArt({ audience }: { audience: Audience }) {
  return (
    <div
      aria-hidden
      className="flex h-44 items-center justify-center rounded-3xl bg-white/60 p-5"
    >
      {audience === "developers" ? (
        <div className="w-full max-w-[220px] overflow-hidden rounded-2xl bg-white shadow-[0_8px_24px_-12px_rgba(20,33,61,0.25)]">
          <div className="flex items-center gap-1 border-b border-navy/10 px-3 py-2">
            <span className="h-1.5 w-1.5 rounded-full bg-blush" />
            <span className="h-1.5 w-1.5 rounded-full bg-butter" />
            <span className="h-1.5 w-1.5 rounded-full bg-mint" />
          </div>
          <div className="space-y-2 p-3">
            <span className="mx-auto block h-2 w-2/3 rounded-full bg-navy/60" />
            <span className="mx-auto block h-2 w-1/2 rounded-full bg-navy/20" />
            <span className="mx-auto block h-5 w-16 rounded-full bg-brand" />
          </div>
        </div>
      ) : (
        <div className="grid w-full max-w-[170px] grid-cols-2 gap-2.5">
          <span className="aspect-[4/3] rounded-2xl bg-white" />
          <span className="aspect-[4/3] rounded-2xl bg-brand/80" />
          <span className="aspect-[4/3] rounded-2xl bg-brand/30" />
          <span className="aspect-[4/3] rounded-2xl bg-white" />
        </div>
      )}
    </div>
  );
}

function ProductCard({ product }: { product: Product }) {
  return (
    <Link
      href={`/products/${product.slug}`}
      className={cn(
        "group flex min-w-0 flex-col rounded-[28px] p-5 transition-transform sm:rounded-[36px] sm:p-6 duration-300 hover:-translate-y-1",
        toneClass[product.tone],
      )}
    >
      <ProductArt audience={product.audience} />
      <div className="mt-5 flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-white px-3 py-1 text-[13px] font-bold text-brand">
          {audienceLabels[product.audience]}
        </span>
        {product.status === "coming-soon" && (
          <span className="rounded-full bg-navy px-3 py-1 text-[13px] font-bold text-cream">
            Coming soon
          </span>
        )}
      </div>
      <h3 className="mt-3 font-display text-2xl font-semibold leading-tight sm:text-[26px] tracking-[-0.6px]">
        {product.name}
      </h3>
      <p className="mt-2 text-[16px] text-ink">{product.description}</p>
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 pt-1">
        <span className="font-display text-xl font-semibold">
          {product.status === "live"
            ? `From $${fromPrice(product)}`
            : "Notify me"}
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-sm font-semibold transition-colors group-hover:text-brand">
          {product.status === "live" ? "View" : "Learn more"}
          <ArrowUpRight className="h-4 w-4" strokeWidth={2.4} />
        </span>
      </div>
    </Link>
  );
}

export function ShopGrid({ products }: { products: Product[] }) {
  const [filter, setFilter] = useState<Filter>("all");
  const shown =
    filter === "all" ? products : products.filter((p) => p.audience === filter);

  if (products.length === 0) {
    return (
      <div className="rounded-[28px] bg-white px-6 py-12 text-center sm:rounded-[36px] sm:px-8 sm:py-14">
        <p className="font-display text-2xl font-semibold">
          Our first products are on the way
        </p>
        <p className="mx-auto mt-2 max-w-[460px] text-ink">
          We&apos;re putting the finishing touches on our first templates and
          illustrations. Join the launch list and we&apos;ll tell you the
          moment they land.
        </p>
        <Link
          href="/#join"
          className="mt-6 inline-flex h-12 items-center rounded-full bg-brand px-7 font-display text-base font-semibold text-white transition-colors hover:bg-[#0742a8]"
        >
          Join the launch list
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div
        role="group"
        aria-label="Filter products"
        className="flex flex-wrap gap-2.5"
      >
        {filters.map((f) => (
          <button
            key={f.id}
            type="button"
            aria-pressed={filter === f.id}
            onClick={() => setFilter(f.id)}
            className={cn(
              "h-11 rounded-full px-5 text-[15px] font-semibold transition-colors",
              filter === f.id
                ? "bg-brand text-white"
                : "bg-white text-navy hover:text-brand",
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      <p className="sr-only" aria-live="polite">
        {shown.length} {shown.length === 1 ? "product" : "products"} shown
      </p>

      {shown.length > 0 ? (
        <div className="mt-6 grid gap-5 sm:mt-8 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((p) => (
            <ProductCard key={p.slug} product={p} />
          ))}
        </div>
      ) : (
        <div className="mt-8 rounded-[28px] bg-white px-6 py-12 text-center sm:rounded-[36px] sm:px-8 sm:py-14">
          <p className="font-display text-2xl font-semibold">
            Nothing here yet
          </p>
          <p className="mx-auto mt-2 max-w-[420px] text-ink">
            We&apos;re working on more. Join the launch list and we&apos;ll
            tell you when it lands.
          </p>
          <Link
            href="/#join"
            className="mt-6 inline-flex h-12 items-center rounded-full bg-brand px-7 font-display text-base font-semibold text-white transition-colors hover:bg-[#0742a8]"
          >
            Join the launch list
          </Link>
        </div>
      )}
    </div>
  );
}
